/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 *
 * The ERE price, as one dated series — and the overlay that makes that series
 * the single source of every euro figure on the site.
 *
 * Before this module the price lived in seven places (see the price-update
 * runbook): a code constant, a CMS number per environment, a free-text
 * "peildatum" line, two article copies, and the fallbacks under them. The July
 * refactor made the CMS number lead, which removed the worst of it but left
 * the number itself undated and unhistoried: nothing on the site said when it
 * was set, from what, or what it was before — and NL and EN drifted apart
 * because they were two fields.
 *
 * So the price is now a SERIES on disk (DATA_DIR/ere_price_history.json), one
 * entry per observation: the date it applies to, the rekenwaarde every
 * calculation uses, the market band around it, and where it came from. The
 * newest entry is overlaid onto the merged translations at read time by
 * {@link applyErePriceToContent}, so every existing consumer — the comparer,
 * the profile pages, the head-to-head, the Kennisbank benchmark card, the
 * rekenvoorbeelden — keeps reading `how_it_works.example_vars` and can no
 * longer disagree with the price page or with the other language.
 *
 * Plain ESM JavaScript with no imports (the deepMerge.js / provinces.js
 * pattern) because the overlay has five call sites that cannot share
 * TypeScript: GET /api/content (server/routes/content.ts), the SPA-fallback
 * render (server/spaRenderer.ts), the shared head mirror (server/seoMeta.ts),
 * the bake (scripts/prerender.mjs) and the feed translations
 * (server/routes/crawlerFiles.ts).
 * server/__tests__/erePrice.test.ts pins that all five call it — a mirror that
 * forgets does not fail, it silently serves a stale price on one surface,
 * which is the exact failure this module exists to end.
 */

/** URL segment, both languages (Dutch segments site-wide, like /kennisbank). */
export const ERE_PRICE_SEGMENT = 'ere-prijs';

/**
 * Path of the price page in one language.
 *
 * @param {string} lang
 * @returns {string}
 */
export function erePricePath(lang) {
  return `/${lang}/${ERE_PRICE_SEGMENT}`;
}

/** Newest first would put "current" at index 0, but a chart reads left to right. */
const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;

/** Entries beyond this are dropped oldest-first — 20 years of monthly readings. */
export const ERE_PRICE_MAX_ENTRIES = 240;

/** @param {unknown} v @param {number} max @returns {string} */
const str = (v, max) => (typeof v === 'string' ? v.trim().slice(0, max) : '');
/** @param {unknown} v @returns {number | null} */
const num = (v) => {
  const n = typeof v === 'number' ? v : Number(v);
  return Number.isFinite(n) ? n : null;
};

/**
 * One reading of the market.
 *
 * @typedef {object} ErePriceEntry
 * @property {string} date       Peildatum, `YYYY-MM-DD` — the day the reading applies to.
 * @property {number} price      Rekenwaarde in €/ERE: THE number every calculation uses.
 * @property {number} low        Bottom of the market band shown next to it.
 * @property {number} high       Top of that band.
 * @property {string} [source]   Who published it ("Flowy Energy prijsgrafiek").
 * @property {string} [sourceUrl] Where, as an http(s) URL.
 * @property {string} [note]     One line of context, editorial, optional.
 */

/**
 * Clean one entry, or null when it cannot be trusted.
 *
 * A band that does not contain the rekenwaarde is widened rather than
 * rejected: a stale low/high next to a fresh price is an editing slip, and
 * rendering "€ 0,45 – € 0,51" beside a rekenwaarde of € 0,60 is worse than
 * quietly widening the band to include it.
 *
 * @param {unknown} raw
 * @returns {ErePriceEntry|null}
 */
export function normalizeErePriceEntry(raw) {
  if (!raw || typeof raw !== 'object') return null;
  const r = /** @type {Record<string, unknown>} */ (raw);
  const date = str(r.date, 10);
  if (!ISO_DATE.test(date)) return null;
  const price = num(r.price);
  if (price === null || price <= 0) return null;
  const low = num(r.low);
  const high = num(r.high);
  const sourceUrl = str(r.sourceUrl, 500);
  // Four decimals at most: the cent-exact chain in src/lib/earnings.ts takes
  // the price in ten-thousandths and formatErePrice prints a reading at its
  // own precision, so a fifth decimal would be dropped silently by the one
  // and printed as noise by the other. No live reading has more than four.
  /** @param {number} x */
  const r4 = (x) => Math.round(x * 10000) / 10000;
  /** @type {ErePriceEntry} */
  const entry = {
    date,
    price: r4(price),
    low: r4(Math.min(low === null || low <= 0 ? price : low, price)),
    high: r4(Math.max(high === null || high <= 0 ? price : high, price)),
  };
  const source = str(r.source, 160);
  if (source) entry.source = source;
  if (/^https?:\/\//i.test(sourceUrl)) entry.sourceUrl = sourceUrl;
  const note = str(r.note, 400);
  if (note) entry.note = note;
  return entry;
}

/**
 * Clean a whole series: valid entries only, one per date (a later duplicate
 * wins — re-entering a month is a correction, not a second reading), sorted
 * oldest first, capped.
 *
 * @param {unknown} raw
 * @returns {ErePriceEntry[]}
 */
export function normalizeErePriceEntries(raw) {
  if (!Array.isArray(raw)) return [];
  const byDate = new Map();
  for (const item of raw) {
    const entry = normalizeErePriceEntry(item);
    if (entry) byDate.set(entry.date, entry);
  }
  const sorted = [...byDate.values()].sort((a, b) =>
    a.date < b.date ? -1 : a.date > b.date ? 1 : 0
  );
  return sorted.slice(Math.max(0, sorted.length - ERE_PRICE_MAX_ENTRIES));
}

/**
 * @typedef {object} ErePriceFile
 * @property {1} version
 * @property {ErePriceEntry[]} entries oldest first
 */

/**
 * @param {unknown} raw
 * @returns {ErePriceFile}
 */
export function normalizeErePriceFile(raw) {
  const r = raw && typeof raw === 'object' ? /** @type {Record<string, unknown>} */ (raw) : {};
  return { version: 1, entries: normalizeErePriceEntries(r.entries) };
}

/**
 * The reading in force: the newest entry, or null for an empty series. Never
 * "today's" entry — a series whose last reading is three months old is stale
 * data, and the page says so with its date rather than hiding it.
 *
 * @param {ErePriceEntry[]|ErePriceFile|null|undefined} input
 * @returns {ErePriceEntry|null}
 */
export function currentErePriceEntry(input) {
  const entries = Array.isArray(input) ? input : (input?.entries ?? []);
  return entries.length ? entries[entries.length - 1] : null;
}

/**
 * Overlay the reading in force onto a merged translations object.
 *
 * Returns a NEW object — the caller's content is often a module-level cache
 * shared between requests, and mutating it would leak one language's overlay
 * into the other. Only the two touched branches are cloned; everything else is
 * shared by reference, so this stays cheap enough to run per request.
 *
 * Absent series → the content is returned unchanged, so a fresh environment
 * with no price file still renders whatever the CMS holds. That is also the
 * migration path: the CMS number keeps working until the first entry lands.
 *
 * Source label and URL are only overlaid when the entry carries them: a series
 * entered without provenance must not blank out a source line an editor wrote.
 *
 * @param {Record<string, any>} content merged baseline + i18n_admin, one language
 * @param {ErePriceEntry[]|ErePriceFile|null|undefined} series
 * @returns {Record<string, any>}
 */
export function applyErePriceToContent(content, series) {
  const entry = currentErePriceEntry(series);
  if (!entry || !content || typeof content !== 'object') return content;
  const how =
    content.how_it_works && typeof content.how_it_works === 'object' ? content.how_it_works : {};
  const vars = how.example_vars && typeof how.example_vars === 'object' ? how.example_vars : {};
  return {
    ...content,
    how_it_works: {
      ...how,
      example_vars: {
        ...vars,
        ere_price_eur: entry.price,
        ere_price_low_eur: entry.low,
        ere_price_high_eur: entry.high,
      },
      price_indicator_asof_iso: entry.date,
      ...(entry.source ? { price_indicator_source: entry.source } : {}),
      ...(entry.sourceUrl ? { price_indicator_source_url: entry.sourceUrl } : {}),
    },
  };
}

/**
 * Change against the previous reading, as a fraction (0.04 = +4%). Null when
 * there is nothing to compare against — never 0, which would read as "the
 * price held steady" when the truth is "this is the first reading".
 *
 * @param {ErePriceEntry[]} entries oldest first
 * @returns {number|null}
 */
export function erePriceChange(entries) {
  if (!Array.isArray(entries) || entries.length < 2) return null;
  const last = entries[entries.length - 1];
  const prev = entries[entries.length - 2];
  if (!prev.price) return null;
  return (last.price - prev.price) / prev.price;
}

/**
 * How finely the dates of a series must be shown: 'month' ("augustus 2026")
 * while no two readings share a month, 'day' ("28 aug 2026") as soon as they
 * do. The page was written for one reading a month; a weekly series under the
 * month-only label repeats "juli 2026" four times down the table and along the
 * chart's axis and tells the reader nothing. Decided once, from the data, so
 * the table, the chart, its summary and the peildatum line cannot disagree.
 *
 * @param {Array<{date: string}>} entries
 * @returns {'month'|'day'}
 */
export function erePriceDateGranularity(entries) {
  if (!Array.isArray(entries)) return 'month';
  const months = new Set();
  for (const e of entries) {
    const month = typeof e?.date === 'string' ? e.date.slice(0, 7) : '';
    if (months.has(month)) return 'day';
    months.add(month);
  }
  return 'month';
}
